import React from 'react';
import { UserStatus } from '~/graphql/types';
import useUsers from '~/hooks/useUsers';
import { type EmailOption } from '~/components/organism/SendEmailBlock/components/MessageHeader/hooks/useGetEmailOptions';
import { useEffect } from 'react';
import MultiSelectEmailInput from '~/components/molecule/MultiSelectEmailInput';
import Catalog from '~/Catalog';

export const text = {
  invalidEmail: 'Ongeldig e-mailadres in de lijst',
};

type Props = {
  /** The office id */
  officeId: string | null;

  /** The selected options in the user selection input */
  selectedOptions: Array<EmailOption>;

  /** Sets the selected options for the user selection input */
  setSelectedOptions: (options: Array<EmailOption>) => void;
};

const UserSelection: React.FC<Props> = ({
  officeId,
  selectedOptions,
  setSelectedOptions,
}) => {
  const usersOutsideOfOffice = useUsers({
    statuses: [UserStatus.Active],
  }).filter(
    user =>
      !user.officeRelations.some(relation => relation.officeId === officeId),
  );

  const onAsyncSearch = async (searchedValue: string) => {
    const userOptions: Array<EmailOption> = [];

    usersOutsideOfOffice.forEach(user => {
      const { name, email, id } = user;

      if (!email) return;

      const checkEmail = email.toLowerCase().includes(searchedValue);
      const checkName = name.toLowerCase().includes(searchedValue);

      if (checkEmail || checkName) {
        const showedLabel = `${name} (${email})`;

        userOptions.push({
          label: showedLabel,
          key: id,
          payload: {
            namedEmail: { email: email, name: name },
            filterLabel: showedLabel,
            tagLabel: showedLabel,
          },
        });
      }
    });

    return userOptions;
  };

  useEffect(() => {
    setSelectedOptions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeId]);

  return (
    <MultiSelectEmailInput
      selectedOptions={selectedOptions}
      onSelected={option => {
        setSelectedOptions([...selectedOptions, option]);
      }}
      onRemoved={option => {
        setSelectedOptions(selectedOptions.filter(o => o.key !== option.key));
      }}
      onAsyncSearch={onAsyncSearch}
      externalErrors={
        selectedOptions.some(({ isValid }) => isValid === false)
          ? [text.invalidEmail]
          : []
      }
      width="100%"
      label={{ text: Catalog.userLabel }}
      disabled={!officeId}
      size="large"
      dataTestId="user-selection-input"
    />
  );
};

export default UserSelection;
